// =====================================================================
// TIER 1 — the envelope. One constraint solve per village address.
//
// Migration (§11): a person's origin address is fixed for life at
// creation — it never changes, and it's what genealogy/parentage decode
// from. Whether they end their days resident in that same village or
// somewhere else is a separate fact (`emigrated`/`emigrateTo`), decided
// once, unilaterally, by their OWN village's own solve — never by the
// destination reaching back to mutate them. That one-directional design is
// what keeps this acyclic: a village's solve may read another village's
// already-resolved envelope (to find real immigrants who named it as their
// destination), but a village never needs its own future destination to
// resolve itself, so there is nothing for the dependency graph to cycle on.
//
// To keep that dependency bounded no matter how large a villageIdx is,
// local migration only flows within a small, fixed-size cluster of
// neighbouring villages (rank.ts) — so resolving any one village never
// triggers more than a handful of neighbour solves, not a chain back to
// village zero. Long-distance migration (§11) is deliberately looser: it
// still produces a real, resolvable destination address, but — since the
// address space is unbounded — the destination doesn't scan the world
// looking for who might have arrived, so it's narrated on the origin side
// only, the same way a medieval parish register usually knows a daughter
// "married out" without knowing much more.
//
// Cross-village identity (§ canonical identity): when a real emigrant is
// pulled into a destination village, the destination adds a RESIDENCE
// record for her, carrying `origin`+`originId` — an address-based pointer
// to her one canonical natal record. The two records never disagree on
// birth, name, or death (death is copied verbatim from the origin's own
// roll), and identity.ts resolves either record to the other, so marriage
// and residence are cross-village facts rather than forked lives.
// =====================================================================

import {
  celibacyMult,
  EXOGAMY_SHARE,
  emigrationMult,
  HOLDINGS_FULL,
  holdingsAt,
  holdingsOf,
  marriageAgeShift,
  VACANCY_EXOGAMY_BONUS,
  VACANT_HOLDINGS,
  WAIT_FOR_HOLDING,
} from "./capacity.js";
import { CLASS_INFO, CLASSES, ceilingPressure, hasCeiling, URBAN_CLASSES } from "./data/classes.js";
import { demographyOf } from "./data/demography.js";
import { placeOf } from "./data/placeNames.js";
import { REGIONS } from "./data/regions.js";
import { DEARTH, fertilityMult, harvestAt, marriageDeferral } from "./harvest.js";
import { addrHash, makeRng, personStream } from "./hash.js";
import { type HarvestReader, rollDeath, warAt } from "./mortality.js";
import { clusterBase, clusterOffset, higherRankRegions, LOCAL_CLUSTER, longDistanceDestination } from "./rank.js";
import { assignServiceMasters, resolveServiceSpells } from "./service.js";
import { settlementTypeOf } from "./settlement.js";
import { tenementsOf } from "./tenement.js";
import type { Address, Couple, Death, Envelope, Person, Sex, SocialClass } from "./types.js";
import { cacheClear, cacheGet, cacheSet, cacheSize } from "./villageCache.js";
import { riskTradeOf, rollDownwardMobility, rollMobility, rollService, rollVocation } from "./villageMobility.js";
import {
  CONSANGUINITY_PENALTY,
  consanguinityCost,
  consanguinityPenalty,
  eldestSonOf,
  grandparentsOf,
  isAffinal,
  isConsanguineous,
  isHeir,
  sharesGrandparent,
} from "./villageRules.js";

// § downward mobility, partible custom: how much of the full non-heir
// downgrade pressure a younger son feels where the land is divided rather
// than passed whole. Well short of 1 — he does inherit something, and that
// is the whole difference between the two customs — but not 0, which is
// what it silently was before, and which left France and Tuscany with no
// counterweight to the class ratchet at all.
export const PARTIBLE_SUBDIVISION = 0.75;

// § the register's own edge. The last year the Tier-1 solve generates births
// for — every couple's childbearing stops here, while deaths of course go on.
// That makes the years after it a fact about where generation stops rather
// than about the village, and the browsable window (ui/render.ts's
// VILLAGE_YEAR_MAX) is pinned to it for exactly that reason: it used to run
// to 1500 against a cap of 1495, and the population curve fell off a cliff
// over those last five years — births zero, burials as usual — which read as
// a village dying out when it was only the register closing. Exported so the
// two can never drift apart again.
export const GENERATION_LAST_YEAR = 1495;

export { ENVELOPE_CACHE_LIMIT } from "./villageCache.js";
export { isHeir } from "./villageRules.js";

export function resolveVillage(worldSeed: number, regionKey: string, villageIdx: number): Envelope {
  const key = `${worldSeed}/${regionKey}/${villageIdx}`;
  const cached = cacheGet(key);
  if (cached) return cached;
  const env = solveVillage(worldSeed, regionKey, villageIdx);
  cacheSet(key, env);
  return env;
}

export function clearEnvelopeCache(): void {
  cacheClear();
}

export function envelopeCacheSize(): number {
  return cacheSize();
}

// How many marriage-matching rounds a solve may run, SHARED across every
// call to runMatchingRounds() in a solve — including the several extra
// calls interleaved with remarriage passes (§ remarriage depth), which
// consume more of the budget than the original single-pass design did.
// Generations to 1490 bound the real per-call need to ~8; hitting this
// limit means truncation, which is recorded in Envelope.diagnostics instead
// of silently dropping a lineage.
export const MATCH_ROUND_LIMIT = 40;

// Persons are constructed in two steps at every call site: addPerson()
// assigns the id (and, unless overridden, the origin), then the caller
// immediately rolls and assigns `.death` — rollDeath's own seed depends on
// the id, so id must exist first. The placeholder below is never read.
type NewPersonInput = Omit<Person, "id" | "death" | "origin"> & { origin?: Address | null };
const PLACEHOLDER_DEATH: Death = { year: 0, age: 0, cause: "infancy" };

// A person's trade-hazard category (§ occupational mortality): rolled once,
// deterministically, from a stream independent of the shared village `rng`
function solveVillage(worldSeed: number, regionKey: string, villageIdx: number): Envelope {
  const region = REGIONS[regionKey];
  const demo = demographyOf(regionKey);
  const vHash = addrHash(worldSeed, [regionKey, "village", villageIdx]);
  const rng = makeRng(vHash);
  const origin: Address = { regionKey, villageIdx };
  // § plague waves: handed to every rollDeath below so a wave's arrival year
  // is a fact about THIS village, shared by everyone who lived through it.
  const villageAddr = { worldSeed, villageIdx };
  // § the harvest (harvest.ts): the region's yield series, read at the two
  // points a bad year actually bit — the mortality walk, and the year a
  // couple set the wedding for. Regional, so every village of the region
  // starves in the same years, which is what made a dearth an event
  // contemporaries could name.
  const yieldAt: HarvestReader = (year) => harvestAt(worldSeed, regionKey, year);
  /** The wedding year, pushed back out of a hunger year into the next one
   * the couple could afford. Bounded by marriageDeferral itself, so this
   * can never spiral past two years — the generational restraint is the
   * preventive check's job, not the weather's. */
  /** § the harvest: the most a bad year may push a wedding, in total.
   *
   * One year, which is both the defensible reading — a couple waits for
   * the NEXT harvest and marries into it — and the value the aggregate
   * bands want. At two the marriage metrics drifted out of every band
   * demography.test.ts holds (the Mediterranean squeeze, the marrying-out
   * share, the peasant share of the village), because a delay of two years
   * on a fifteenth-century marriage window is a large fraction of it. */
  const MAX_HARVEST_DEFERRAL = 1;
  function deferForHarvest(year: number, deadline?: number): number {
    // Capped at two years TOTAL, not two per bad year. Left to compound,
    // a run of failures walked a wedding four and five years down the
    // calendar, which is not what a dearth does: a couple waits for the
    // next harvest they can afford and marries into it, and if the bad
    // years keep coming they marry anyway. The generational restraint is
    // the preventive check's job (capacity.ts), not the weather's.
    let y = year;
    for (let guard = 0; guard < 3 && y - year < MAX_HARVEST_DEFERRAL; guard++) {
      const push = marriageDeferral(yieldAt(y));
      if (!push) break;
      y = Math.min(y + push, year + MAX_HARVEST_DEFERRAL);
    }
    // A postponement past the end of a life is not a postponement, it is a
    // marriage that never happened — and modelling it as one quietly turned
    // the Mediterranean marriage squeeze inside out. There the brides are
    // young and numerous and the surplus falls on the MEN (the catasto's
    // near-universal female marriage beside a great many unmarried men);
    // deferring a bride past her own death year instead pushed permanent
    // celibacy onto the women, inverting the one pattern demography.test.ts
    // exists to hold. So the wait is capped by the life it is asked of.
    return deadline != null && y >= deadline ? Math.min(year, deadline - 1) : y;
  }

  // § the preventive check (capacity.ts): how much of the village's land is
  // already spoken for, as of a given year. Read at the two moments a real
  // community's answer to that question actually bit — when a man was
  // deciding whether, and how long to wait, to marry, and when someone
  // unmatched was deciding whether to stay — so the population regulates
  // itself toward its own ceiling instead of compounding freely.
  //
  // A tenement counts as held while EITHER spouse of the union currently on
  // it is alive: a widow kept the holding, and a village full of widows was
  // still a full village. Only a spouse's current union counts, so a
  // remarriage moves a household rather than founding a second one.
  //
  // Counting couples created SO FAR is exactly right: births and marriages
  // are generated in order, so every union that existed in `year` already
  // does by the time any decision dated to that year is taken. `emigrated`
  // is only set during the emigration passes themselves, where it correctly
  // makes the pressure ease as each departure is decided.
  function currentUnionAt(p: Person, year: number): number | null {
    let found: number | null = null;
    for (const ci of p.unions ?? []) if (couples[ci].year <= year) found = ci;
    return found;
  }
  // § the tenement (tenement.ts): the village's actual holdings, each with
  // a size, rather than the bare count the preventive check measured
  // against. The stock and the count are the same number by construction —
  // tenementsOf is built off holdingsOf — so nothing about the check
  // changes; what changes is that a household now stands on a NAMED piece
  // of ground that outlives it.
  const tenements = tenementsOf(worldSeed, regionKey, villageIdx);

  /** The years a household actually sits on its ground: from the wedding
   * until the last of the pair is dead, gone, or has moved to a later
   * union's holding.
   *
   * Both deaths are already rolled by the time anybody marries, so this is
   * knowable at assignment — which matters, because couples are NOT created
   * in chronological order. The marriage matching runs in rounds, so a
   * couple built later can have an earlier wedding year than one built
   * before it, and a check that only asked "is this holding free in the
   * wedding year" happily gave the same ground to a household of 1380 that
   * it had already given to one of 1400. Overlapping the whole span is what
   * makes the occupancy actually exclusive. */
  function tenureSpan(ci: number): [number, number] {
    const c = couples[ci];
    const endOf = (p: Person): number => {
      if (p.emigrated) return c.year;
      const end = p.death.year;
      for (const u of p.unions ?? []) if (couples[u].year > c.year) return Math.min(end, couples[u].year);
      return end;
    };
    return [c.year, Math.max(endOf(persons[c.husband]), endOf(persons[c.wife]))];
  }

  /** Free for the whole of [from, to] — nobody else's tenure overlaps it.
   *
   * `exceptCouple` is the household asking; `alsoExcept` is the one it is
   * SUCCEEDING on the same ground, which has to be exempted or the rule
   * blocks itself. A widow remarrying keeps her holding, but at the moment
   * she remarries her new union is not yet on her `unions` list, so her old
   * tenure still looks as though it runs to her death — and her own former
   * household was therefore found sitting on the ground she was trying to
   * stay on. Measured: the keep-your-holding rule fired exactly zero times
   * before this. */
  function tenementFree(t: number, from: number, to: number, exceptCouple: number, alsoExcept = -1): boolean {
    for (let ci = 0; ci < couples.length; ci++) {
      if (ci === exceptCouple || ci === alsoExcept || couples[ci].tenement !== t) continue;
      const [a, b] = tenureSpan(ci);
      if (from <= b && a <= to) return false;
    }
    return true;
  }

  /** The ground a new household takes up.
   *
   * The order is the one the court rolls show. A surviving spouse
   * remarrying keeps the holding they already sit on — a remarriage moves
   * a household, it does not found a second one. Otherwise the heir takes
   * his father's tenement if it has actually fallen vacant, which is what
   * makes succession a fact about a PLACE and not merely about a person.
   * Failing that he takes up whatever is standing empty, and a household
   * with something behind it takes the better ground. And where nothing is
   * free he holds nothing at all: an undersettle on another man's land,
   * which is a real category the surveys record and the bare count could
   * not express. */
  function assignTenement(H: Person, W: Person, year: number, ci: number): number | null {
    // The span this household would occupy, computed the same way every
    // other tenure is (the couple is not on `couples` yet, so this mirrors
    // tenureSpan against the record about to be pushed).
    const to = Math.max(H.emigrated ? year : H.death.year, W.emigrated ? year : W.death.year);
    // A surviving spouse remarrying stays on the ground they already hold.
    for (const p of [H, W]) {
      const prev = currentUnionAt(p, year - 1);
      const held = prev != null ? couples[prev].tenement : undefined;
      if (held != null && tenementFree(held, year, to, ci, prev!)) return held;
    }
    // The father's holding, if he is gone from it and this son is the heir.
    if (H.father >= 0) {
      const fatherUnion = currentUnionAt(persons[H.father], year - 1) ?? persons[H.father].unions?.[0];
      const paternal = fatherUnion != null ? couples[fatherUnion].tenement : undefined;
      if (paternal != null && isHeir(persons, region, regionKey, H) && tenementFree(paternal, year, to, ci)) return paternal;
    }
    const free = tenements.filter((t) => tenementFree(t.idx, year, to, ci));
    if (!free.length) return null;
    // A gentry or merchant household takes the best ground going; a serf
    // takes what is left. Reading the wealth grade rather than the class
    // keeps this in step with everything else that grades a household.
    const wealth = CLASS_INFO[H.cls].wealth;
    if (wealth >= 3) return free[0].idx;
    if (wealth === 1) return free[free.length - 1].idx;
    return free[Math.floor(free.length / 2)].idx;
  }

  function pressureAt(year: number): number {
    let held = 0;
    for (let ci = 0; ci < couples.length; ci++) {
      const c = couples[ci];
      if (c.year > year) continue;
      const H = persons[c.husband];
      const W = persons[c.wife];
      const hHere = H.death.year > year && !H.emigrated;
      const wHere = W.death.year > year && !W.emigrated;
      if (hHere ? currentUnionAt(H, year) === ci : wHere && currentUnionAt(W, year) === ci) held++;
    }
    return held / holdingsAt(worldSeed, regionKey, villageIdx, year);
  }

  // § the estate ceiling (data/classes.ts): what share of the village's
  // living souls a class already holds, as of a year. Read at the one moment
  // it bites — a son being born into an estate that may or may not have room
  // for another household of its kind — so the class structure regulates
  // itself against the village instead of compounding on the luck of which
  // founder lineages happened to survive.
  //
  // The denominator has a floor: in the founding years a village of a dozen
  // souls would otherwise read its single gentry couple as a sixth of the
  // parish and demote their sons on the strength of a sample of twelve.
  //
  // Measuring a share means walking the whole village, and this is called as
  // every child is born, so it is guarded by hasCeiling: the peasantry has no
  // ceiling and is most of the village, so most births skip the walk entirely.
  function ceilingMult(cls: SocialClass, year: number): number {
    if (!hasCeiling(cls, settlement)) return 1;
    let held = 0;
    let alive = 0;
    for (const q of persons) {
      if (q.birth > year || q.death.year <= year) continue;
      alive++;
      if (q.cls === cls) held++;
    }
    return ceilingPressure(cls, settlement, held / Math.max(alive, 30));
  }

  // § exogamy ceiling (capacity.ts): a running count of the marriages that
  // brought a spouse in from outside the parish, against which both import
  // paths below check themselves. Anyone turned away here isn't married off
  // some other way — they fall through to the ordinary wait-or-leave
  // machinery, which is exactly the outflow the imports used to suppress.
  let exogamous = 0;
  function exogamyAllowed(pressure: number): boolean {
    const ceiling = EXOGAMY_SHARE * (pressure < VACANT_HOLDINGS ? VACANCY_EXOGAMY_BONUS : 1);
    // The first handful of marriages in a village have no meaningful share
    // to measure yet; the floor keeps the very first incomer from being
    // rejected on a sample of one.
    return exogamous < ceiling * Math.max(couples.length, 8);
  }

  const place = placeOf(worldSeed, regionKey, villageIdx);
  const persons: Person[] = []; // id-indexed
  const couples: Couple[] = [];

  function addPerson(p: NewPersonInput): Person {
    const full: Person = { ...p, id: persons.length, origin: p.origin !== undefined ? p.origin : origin, death: PLACEHOLDER_DEATH };
    persons.push(full);
    return full;
  }

  // § dual surnames (Catalonia): the Iberian convention of a compound
  // surname — the father's own FIRST surname plus the mother's own FIRST
  // surname — fixed at birth and never replaced at marriage. `surnameLine`
  // strips a compound surname back down to the one component that is
  // actually transmissible; `drawSurname`/`childSurname` are single-name
  // elsewhere, compound only here.
  function surnameLine(s: string): string {
    const i = s.indexOf(" i ");
    return i < 0 ? s : s.slice(0, i);
  }
  function drawSurname(pool?: string[]): string {
    const one = () => (pool?.length ? pool.splice(rng.int(0, pool.length - 1), 1)[0] : rng.pick(region.surnames));
    return regionKey === "catalonia" ? `${one()} i ${one()}` : one();
  }
  function childSurname(H: Person, W: Person): string {
    return regionKey === "catalonia" ? `${surnameLine(H.surname)} i ${surnameLine(W.surname)}` : H.surname;
  }

  // Founders: G0 couples born 1235–1275, already married. Their pre-history
  // is outside the register ("the register begins in 1290").
  //
  // § the preventive check: how many of them there are is the village's own
  // holdings (capacity.ts) less a few still standing open, not an
  // independent roll. A settlement that starts with a third of its
  // tenements empty spends its first three generations filling them — which
  // put the whole growth phase squarely on top of the fourteenth century,
  // and left villages larger after the Black Death than before it. The real
  // thing was already at its limit by 1300: that is what the land hunger,
  // the assarting of marginal ground and the Great Famine all record.
  const founderCouples = Math.max(6, holdingsOf(worldSeed, regionKey, villageIdx) - rng.int(1, 4));
  const surnamePool = region.surnames.slice();
  // § settlement: a market town's founders skew toward the trades a town
  // existed to house (data/classes.ts's URBAN_CLASSES) rather than the
  // ordinary rural mix. One rng.weighted() call either way, so this never
  // changes the shared stream's draw count — only which table it reads
  // from. Native children inherit class at birth and only move class via
  // the existing mobility rolls (villageMobility.ts), so this single hook
  // is what drives the whole village's eventual class/wealth/occupation
  // mix toward "urban" or "rural".
  const settlement = settlementTypeOf(worldSeed, regionKey, villageIdx);
  const classTable = settlement === "urban" ? URBAN_CLASSES : CLASSES;
  for (let i = 0; i < founderCouples; i++) {
    const cls = rng.weighted(classTable);
    const wealth = CLASS_INFO[cls].wealth;
    const surname = drawSurname(surnamePool);
    const hb = rng.int(1235, 1272);
    const wb = hb + rng.int(1, 6);
    const H = addPerson({ name: rng.pick(region.maleNames), surname, sex: "M", birth: hb, cls, father: -1, mother: -1, founder: true });
    const W = addPerson({
      name: rng.pick(region.femaleNames),
      surname: drawSurname(),
      sex: "F",
      birth: wb,
      cls,
      father: -1,
      mother: -1,
      founder: true,
      incomer: true,
      origin: null,
    });
    H.riskTrade = riskTradeOf(vHash, H.id, H.cls, H.sex);
    W.riskTrade = riskTradeOf(vHash, W.id, W.cls, W.sex);
    H.death = rollDeath(makeRng(personStream(vHash, 7001, H.id)), hb, "M", wealth, region, H.riskTrade, regionKey, villageAddr, yieldAt);
    W.death = rollDeath(makeRng(personStream(vHash, 7001, W.id)), wb, "F", wealth, region, W.riskTrade, regionKey, villageAddr, yieldAt);
    // founders are guaranteed to reach marriage (they existed to found the
    // line) — extend a death that would otherwise fall on or before the
    // marriage year itself, not just one that's short of a fixed age floor.
    // (A fixed floor here previously undershot the marriage-year formula's
    // own range, e.g. a natural death age of 24-25 for H still failed to
    // outlive a marriage age that can itself roll as high as 26.)
    const marriageYear = Math.max(hb + rng.int(22, 26), wb + rng.int(17, 20));
    if (H.death.year <= marriageYear) {
      const age = marriageYear - hb + 1 + rng.int(0, 30);
      H.death = { year: hb + age, age, cause: "disease" };
    }
    if (W.death.year <= marriageYear) {
      const age = marriageYear - wb + 1 + rng.int(0, 30);
      W.death = { year: wb + age, age, cause: "disease" };
    }
    marry(H, W, marriageYear);
  }

  function marry(H: Person, W: Person, year: number): Couple | null {
    // marriage cannot outlive either spouse
    if (year >= H.death.year || year >= W.death.year) return null;
    const c: Couple = { husband: H.id, wife: W.id, year, children: [] };
    if (isConsanguineous(persons, H, W)) c.consanguineous = true;
    const ci = couples.length;
    // § the tenement: which piece of ground this household actually stands
    // on. Assigned here because marry() is the one place a household comes
    // into being, and read back by tenement.ts for the holding's own page.
    const t = assignTenement(H, W, year, ci);
    if (t != null) c.tenement = t;
    couples.push(c);
    if (!H.unions) H.unions = [];
    H.unions.push(ci);
    if (!W.unions) W.unions = [];
    W.unions.push(ci);
    // `spouse`/`marriageYear` keep first-marriage semantics; the full
    // history (remarriage included) lives on `unions`.
    if (H.spouse == null) {
      H.spouse = W.id;
      H.marriageYear = year;
    }
    if (W.spouse == null) {
      W.spouse = H.id;
      W.marriageYear = year;
    }
    return c;
  }

  function makeChild(c: Couple, H: Person, W: Person, y: number): Person {
    const sex: Sex = rng.chance(0.5) ? "M" : "F";
    // § sibling name collision: the per-region name pools are small (a
    // dozen or so per sex — see region.maleNames/femaleNames), so drawing
    // independently for every child of a large family made it common for
    // two or three of them to be alive AT THE SAME TIME sharing a name.
    // Reusing a dead sibling's name for a later child was real medieval
    // practice; two LIVING siblings sharing one was not (bar rare royal
    // exceptions this model doesn't represent) — so exclude only the names
    // still held by a full sibling (this couple's own children so far)
    // who's still alive at this child's own birth year. Falls back to the
    // full pool only if every name is already spoken for (a large family
    // in a small-pool region), rather than ever picking from an empty list.
    const namePool = sex === "M" ? region.maleNames : region.femaleNames;
    const takenByLivingSibling = new Set(
      c.children
        .map((cid) => persons[cid])
        .filter((sib) => sib.sex === sex && sib.death.year > y)
        .map((sib) => sib.name),
    );
    const availableNames = namePool.filter((n) => !takenByLivingSibling.has(n));
    const child = addPerson({
      name: rng.pick(availableNames.length ? availableNames : namePool),
      surname: childSurname(H, W),
      sex,
      birth: y,
      cls: H.cls,
      father: H.id,
      mother: W.id,
    });
    // § the estate ceiling: promotion answers to the same ceiling demotion
    // does, from the other side — a village already full of trading houses
    // makes few more, and one whose gentry line has failed for want of male
    // heirs can raise another. Read at the child's birth year, which is the
    // village the promotion would actually have to fit into.
    rollMobility(vHash, demo, child, (into) => 1 / ceilingMult(into, y));
    // § male out-migration / § downward mobility: both class-transition
    // rolls happen BEFORE riskTradeOf, so the trade-hazard tag it derives
    // always reflects the child's FINAL class, never a stale pre-transition
    // one (occupation narrative in biography.ts reads p.cls fresh anyway,
    // but riskTrade is rolled once here and must agree with it).
    const nonHeirSon = child.sex === "M" && !isHeir(persons, region, regionKey, child);
    // § downward mobility: how hard the class ratchet pushes back on this
    // child. Under impartible custom it is the plain non-heir case — he gets
    // nothing, so he keeps nothing. Under partible custom every son does get
    // a share, which is why isHeir reads them all as heirs and the whole
    // mechanism used to be inert in France and Tuscany; but a share is not
    // the whole, and land divided among brothers every generation is exactly
    // how a partible-inheritance house slid down a rung. So sons after the
    // first face the same rungs at reduced weight rather than none at all.
    const subdividing = !nonHeirSon && child.sex === "M" && region.inheritance === "partible" && !eldestSonOf(persons, child, regionKey);
    const custom = nonHeirSon ? 1 : subdividing ? PARTIBLE_SUBDIVISION : 0;
    // § the estate ceiling (data/classes.ts): and how much room his father's
    // estate still has in this village. Rates that balance on average still
    // let one lucky lineage compound into a village that is an eighth gentry
    // — the ceiling is what actually holds the number of such households to
    // the number of estates there are to hold.
    rollDownwardMobility(vHash, demo, child, custom * ceilingMult(child.cls, y));
    child.riskTrade = riskTradeOf(vHash, child.id, child.cls, child.sex);
    child.death = rollDeath(
      makeRng(personStream(vHash, 7001, child.id)),
      y,
      sex,
      CLASS_INFO[child.cls].wealth,
      region,
      child.riskTrade,
      regionKey,
      villageAddr,
      yieldAt,
    );
    // § male out-migration: a non-heir son — of ANY wealth grade, not just
    // the low-wealth default rollService already covers — is likelier to be
    // sent into service or apprenticeship elsewhere, since he won't inherit.
    rollService(vHash, demo, child, nonHeirSon);
    // § the celibate estate: whether this child was marked out for religion
    // instead. Rolled here, at birth, so the marriage matching below can
    // simply leave them out rather than deciding it mid-loop for one class
    // of one sex — which is what confined the whole institution to sons of
    // `clergyFamily` houses, and left regions with no such houses (Germany,
    // Portugal) without a single religious in them.
    rollVocation(vHash, demo, child, nonHeirSon);
    c.children.push(child.id);
    return child;
  }

  // § multiple births: a modest, stylized twinning rate (not a precise
  // historical figure — well attested to run roughly ~1% of births across
  // pre-modern Europe, with no strong regional signal this model tries to
  // capture) — rolled from a stream independent of the shared `rng` (own
  // namespace 980000, mirroring riskTradeOf/rollMobility's own streams) so
  // adding this never perturbs the existing birth-spacing/matching sequence.
  const TWIN_RATE = 0.012;
  // § multiple births: elevated infant mortality. Twins faced a real excess
  // risk over singletons (lower birth weight, harder delivery) that the
  // shared hazard model (mortality.ts) has no notion of twinning to apply —
  // so this is a modest ADDITIONAL chance layered on afterward, on each
  // twin independently, and only ever turns an already-rolled survival into
  // an infancy death, never the reverse. A deliberately mild bump (see
  // demography.ts's own header comment on preferring mild multipliers over
  // dramatic ones), not a claim to a precise historical excess-mortality rate.
  const TWIN_EXCESS_INFANT_MORTALITY = 0.08;
  function applyTwinExcessMortality(twin: Person): void {
    if (twin.death.age === 0) return;
    const r = makeRng(personStream(vHash, 981000, twin.id));
    if (!r.chance(TWIN_EXCESS_INFANT_MORTALITY)) return;
    twin.death = { year: twin.birth, age: 0, cause: "infancy" };
    // This is the one place in the model where a death already rolled is
    // REPLACED, and makeChild has by now allocated a whole life on the
    // strength of the roll it replaces. § the celibate estate is the part
    // that doesn't clean up after itself: rollVocation refuses anyone who
    // died before 14, but it was asked before this, and the answer stands
    // unless it is withdrawn — which put a cowled infant in the register with
    // a fourteen-year gap between his birth and his tonsure. (`service` needs
    // no such handling: resolveServiceSpells clamps every spell to the
    // servant's own death year and drops the ones that leave nothing.)
    twin.inOrders = undefined;
  }
  function maybeTwin(c: Couple, H: Person, W: Person, y: number, firstborn: Person): void {
    if (c.children.length >= 11) return;
    const r = makeRng(personStream(vHash, 980000, firstborn.id));
    if (!r.chance(TWIN_RATE)) return;
    const twin = makeChild(c, H, W, y);
    firstborn.twinOf = twin.id;
    twin.twinOf = firstborn.id;
    applyTwinExcessMortality(firstborn);
    applyTwinExcessMortality(twin);
  }

  function genChildren(c: Couple, capYear: number): void {
    const H = persons[c.husband],
      W = persons[c.wife];
    const endYear = Math.min(H.death.year, W.death.year, W.birth + 42);
    let y = c.year + rng.int(1, 2);
    // § bridal pregnancy: a first child inside the wedding year — conceived
    // before it, born after. Far commoner than illegitimacy proper (10–30% of
    // first marriages against 1–4% of births) and, where betrothal was itself
    // binding, not the same transgression at all: it is most of what the
    // "bastardy-prone" cases in the record actually were, a couple who
    // married slightly late. Only her FIRST marriage — a widow's remarriage
    // in the same year as a birth would read as her late husband's child.
    // The draw happens either way, so which branch is taken never changes the
    // shared stream's draw count.
    const brideFirstUnion = W.unions?.[0];
    const bridal = rng.chance(demo.bridalPregnancy);
    if (bridal && brideFirstUnion != null && couples[brideFirstUnion] === c) y = c.year;
    while (y < endYear && y <= capYear && c.children.length < 11) {
      const child = makeChild(c, H, W, y);
      maybeTwin(c, H, W, y, child);
      // § lactation interruption: nursing suppressed conception, and it was
      // the single strongest brake on birth spacing in a population with no
      // other — which is why family reconstitution finds intervals after a
      // baby who died at the breast running markedly shorter than intervals
      // after one who lived. The draw happens either way, so which branch is
      // taken never changes the shared stream's draw count.
      const spacing = rng.int(demo.birthSpacing[0], demo.birthSpacing[1]);
      // § the harvest: hunger suppresses conception, and the deficit turns
      // up in the baptisms of the year after — so a bad harvest widens the
      // interval rather than removing a child outright. The draw happens
      // either way, so the branch taken never changes the stream's count.
      const hungry = fertilityMult(yieldAt(y)) < 1 && rng.chance(1 - fertilityMult(yieldAt(y)));
      y += (child.death.age === 0 ? demo.birthSpacing[0] : spacing) + (hungry ? 1 : 0);
    }
  }

  // Real immigrant lookup for exogamous marriages: reads (never mutates)
  // already-resolved lower-rank cluster-mates' envelopes for a native woman
  // who named THIS village as her emigration destination. Bounded to the
  // local cluster, so this can only ever trigger up to LOCAL_CLUSTER-1
  // neighbour solves, never an unbounded chain.
  const immigrantTaken = new Set<string>();
  function pullImmigrant(sex: Sex, wantYear: number, ageLo: number, ageHi: number): { srcIdx: number; cand: Person } | null {
    const base = clusterBase(villageIdx);
    for (let srcIdx = base; srcIdx < villageIdx; srcIdx++) {
      if (srcIdx < 0) continue;
      const srcEnv = resolveVillage(worldSeed, regionKey, srcIdx); // strictly lower rank: safe, terminates
      for (const cand of srcEnv.persons) {
        if (cand.sex !== sex || !cand.emigrated || !cand.emigrateTo) continue;
        if (cand.emigrateTo.regionKey !== regionKey || cand.emigrateTo.villageIdx !== villageIdx) continue;
        const key = `${srcIdx}:${cand.id}`;
        if (immigrantTaken.has(key)) continue;
        const ageAt = wantYear - cand.birth;
        if (ageAt < ageLo || ageAt > ageHi) continue;
        if (cand.death.year <= wantYear) continue;
        immigrantTaken.add(key);
        return { srcIdx, cand };
      }
    }
    return null;
  }
  const pullImmigrantBride = (wantYear: number, ageLo: number, ageHi: number) => pullImmigrant("F", wantYear, ageLo, ageHi);
  // § male out-migration: symmetric to pullImmigrantBride, but for a local
  // woman who found no local husband — she marries a REAL non-heir man who
  // left his own (lower-rank cluster-mate) village, rather than either
  // village fabricating anyone. Reuses the same `immigrantTaken` set: keys
  // are `srcIdx:id` scoped to one sex's search since only opposite-sex
  // candidates are ever matched, so bride and groom keys never collide.
  const pullImmigrantGroom = (wantYear: number, ageLo: number, ageHi: number) => pullImmigrant("M", wantYear, ageLo, ageHi);

  // Generate children for every couple (in couple-creation order — this is a
  // work queue, so children of later marriages get processed too).
  for (let ci = 0; ci < couples.length; ci++) {
    genChildren(couples[ci], GENERATION_LAST_YEAR);
  }

  // Marriage matching (spec §4): resolved at the envelope tier, in rounds —
  // each round's marriages produce the next generation, which is then
  // matched in the following round, until the genealogy closes (no new
  // eligible persons). Deterministic: rounds process in birth order.
  const processed = new Set<number>();
  let matchingRounds = 0;
  function runMatchingRounds(): void {
    while (matchingRounds < MATCH_ROUND_LIMIT) {
      // § the celibate estate: anyone already marked out for religion
      // (villageMobility.ts's rollVocation, at birth) is not in the marriage
      // market on either side of it — not a candidate, and not someone the
      // truncation diagnostics below should count as an unresolved match.
      const eligible = persons.filter((p) => !p.founder && !p.inOrders && p.death.age >= 16 && p.spouse == null && !processed.has(p.id));
      if (!eligible.length) break;
      matchingRounds++;
      eligible.forEach((p) => {
        processed.add(p.id);
      });
      eligible.sort((a, b) => a.birth - b.birth || a.id - b.id);
      const men = eligible.filter((p) => p.sex === "M");
      const women = persons
        // § the marriage squeeze: `emigrated` as well as `marriedOut`. The two
        // used to be the same fact for a woman — every female departure was a
        // marriage — but a daughter who left for service in a town is
        // `emigrated` without being `marriedOut`, and the interleaved
        // remarriage/matching loop further down would otherwise still marry
        // her here after she had gone.
        .filter((p) => p.sex === "F" && !p.founder && !p.inOrders && p.death.age >= 16 && p.spouse == null && !p.marriedOut && !p.emigrated)
        .sort((a, b) => a.birth - b.birth || a.id - b.id);
      const takenW = new Set<number>();

      for (const M of men) {
        // § calibrated mechanics: never-marry share ~10% on average (was
        // higher, which — compounded with emigration and plague — sank most
        // villages below replacement and emptied them by 1450, far past the
        // ~10% of real villages deserted by 1500).
        // § male out-migration: an heir had a tenement to offer a bride and
        // marries reliably; a non-heir is a somewhat less attractive match.
        const heir = isHeir(persons, region, regionKey, M);
        // § the preventive check (capacity.ts): read as he comes of age, so
        // the village he is actually deciding within is the one that has to
        // find him a holding. It moves both instruments at once — whether he
        // marries at all, and how long he waits.
        const pressure = pressureAt(M.birth + region.marriageM[0]);
        if (rng.chance((heir ? 0.07 : 0.12) * celibacyMult(pressure))) continue; // never marries
        const targetGap = rng.int(1, region.marriageM[0] - region.marriageF[0] + 3);
        const mAge = rng.int(region.marriageM[0], region.marriageM[1]) + marriageAgeShift(pressure);
        // § the preventive check, hard edge (capacity.ts): where every
        // holding in the village is taken there is nowhere to put another
        // household, so he waits for one to fall vacant — and if none does
        // within reach, he never marries, and falls through to the
        // out-migration pass below like any other unmatched man.
        // § the harvest: a couple did not marry in a year when bread was at
        // famine price — the marriage register empties in a dearth and
        // fills again after the next good harvest. Applied before the
        // holdings check, since a man who waits out a bad year then still
        // has to find a tenement at the end of it.
        let wantYear = deferForHarvest(M.birth + mAge, M.death.year);
        const harvestPush = Math.max(0, wantYear - (M.birth + mAge));
        if (pressureAt(wantYear) >= HOLDINGS_FULL) {
          const wait = WAIT_FOR_HOLDING.find((d) => pressureAt(wantYear + d) < HOLDINGS_FULL);
          if (wait === undefined) continue;
          wantYear += wait;
        }
        // best local candidate: right age window, different household, alive at wantYear
        let best: Person | null = null,
          bestScore = 1e9,
          bestPenalty = 0;
        // § consanguinity avoidance: his grandparents, once, rather than
        // rebuilt against every woman in the village (see sharesGrandparent).
        const gM = grandparentsOf(persons, M);
        for (const W of women) {
          if (takenW.has(W.id)) continue;
          if (W.father === M.father && M.father !== -1) continue; // no siblings (paternal)
          if (W.mother === M.mother && M.mother !== -1) continue; // no siblings (maternal — a widow's children by an earlier husband)
          const wAgeAt = wantYear - W.birth;
          // § the marriage squeeze: the preventive check pushes HIS marriage
          // age later (marriageAgeShift, above) while her acceptable age band
          // stayed pinned to her own birth — so a crowded village kept
          // deferring its men into a bride pool that had aged out from under
          // them, and stranded the women it deferred past the window for good.
          // If the check delays the marriage it delays the market, both sides
          // of it. Worst in the Mediterranean, whose window is the narrowest
          // and starts earliest, and whose villages are the most crowded.
          // § the harvest: and the same is true of a delay for a bad year.
          // It is the identical bug in a second guise — a push applied to
          // HIS year while her band stays pinned to her birth — and it
          // reproduced the identical symptom: measured, it moved permanent
          // celibacy in Italy off the men and onto the women, inverting the
          // Mediterranean pattern this model was corrected to produce. If
          // the harvest delays the marriage it delays the market too.
          if (wAgeAt < region.marriageF[0] - 1 || wAgeAt > region.marriageF[1] + 6 + Math.max(0, marriageAgeShift(pressure)) + harvestPush) continue;
          if (W.death.year <= wantYear || M.death.year <= wantYear) continue;
          // § consanguinity avoidance: a first cousin is a last resort, not
          // an equal candidate — the impediment and the cost of the
          // dispensation are what kept these matches rare.
          const penalty = sharesGrandparent(gM, persons, W) ? consanguinityCost(M, W) : 0;
          const score = Math.abs(wAgeAt - (mAge - targetGap)) + penalty;
          if (score < bestScore) {
            bestScore = score;
            best = W;
            bestPenalty = penalty;
          }
        }
        // § consanguinity avoidance, the second half of it. Scoring alone
        // only decides WHICH local woman he takes, and in a pool as closed as
        // a village's it is common for every candidate within his age window
        // to be kin — at which point the penalty changes nothing and he
        // marries a cousin anyway. That is why re-scoring on its own only
        // took cousin matches from 8–14% of couples down to 4–6%.
        //
        // What a man in that position actually did is the thing the branch
        // below already models: he looked outside the parish. So where the
        // best the village can offer is a cousin he is far likelier to fall
        // through to exogamy — unless both houses are propertied, where the
        // match was often the whole point and the dispensation affordable.
        const localChance = bestPenalty === CONSANGUINITY_PENALTY ? 0.15 : 0.8;
        if (best && rng.chance(localChance)) {
          takenW.add(best.id);
          const c = marry(M, best, wantYear);
          if (c) genChildren(c, GENERATION_LAST_YEAR);
        } else {
          // exogamy: prefer a REAL emigrant already recorded in a lower-rank
          // cluster-mate's own envelope (a genuine cross-village pointer, never
          // a new decode) — only fabricate an unaddressable incomer if the
          // local cluster has nobody to offer.
          // most men without a local match DO find a bride outside (skip only ~12%)
          if (M.death.year <= wantYear || rng.chance(0.12)) continue;
          // § exogamy ceiling: past the share a parish really exchanged with
          // its neighbours, he waits rather than founding a household on an
          // incomer — and, being still unmarried, becomes a candidate for
          // the out-migration pass below like any other unmatched man.
          if (!exogamyAllowed(pressure)) continue;
          const pulled = pullImmigrantBride(wantYear, region.marriageF[0] - 1, region.marriageF[1] + 6);
          let W: Person;
          if (pulled) {
            const { srcIdx, cand } = pulled;
            W = addPerson({
              name: cand.name,
              surname: cand.surname,
              sex: "F",
              birth: cand.birth,
              cls: M.cls,
              father: -1,
              mother: -1,
              incomer: true,
              origin: { regionKey, villageIdx: srcIdx },
              originId: cand.id,
            });
            W.death = { ...cand.death }; // copy of the ORIGIN's canonical roll: the two records never disagree on when she died
          } else {
            const wb = wantYear - rng.int(region.marriageF[0], region.marriageF[1]);
            W = addPerson({
              name: rng.pick(region.femaleNames),
              surname: drawSurname(),
              sex: "F",
              birth: wb,
              cls: M.cls,
              father: -1,
              mother: -1,
              incomer: true,
              origin: null,
            });
            W.death = rollDeath(makeRng(personStream(vHash, 7001, W.id)), wb, "F", CLASS_INFO[M.cls].wealth, region, "normal", regionKey, villageAddr, yieldAt);
            if (W.death.year <= wantYear) {
              const deathYear = wantYear + 1 + rng.int(0, 25);
              W.death = { year: deathYear, age: deathYear - wb, cause: "disease" };
            }
          }
          const c = marry(M, W, wantYear);
          if (c) {
            exogamous++;
            genChildren(c, GENERATION_LAST_YEAR);
          }
        }
      }

      // § male out-migration: local women this round's men loop left
      // unmatched get a shot at a REAL immigrant groom — a non-heir man who
      // left a lower-rank cluster-mate village — before falling through to
      // their own eventual emigration pass below. Symmetric to
      // pullImmigrantBride, so a woman's alternative to waiting or leaving
      // herself is marrying someone who actually left another village's own
      // register, not a fabrication on either side.
      for (const W of women) {
        if (takenW.has(W.id) || W.spouse != null || W.marriedOut || W.emigrated) continue;
        // § the preventive check: she waits (or doesn't) for the same reason
        // he does — the men's loop above already shifts her age wherever she
        // was matched there, and this keeps the immigrant-groom route from
        // being the one door that ignores how full the village is.
        const wantYear = deferForHarvest(
          W.birth + rng.int(region.marriageF[0], region.marriageF[1]) + marriageAgeShift(pressureAt(W.birth + region.marriageF[0])),
          W.death.year,
        );
        if (W.death.year <= wantYear) continue;
        // § vacant holdings: a village with tenements standing empty was a
        // village worth coming to, and it is the reason the post-plague
        // countryside refilled from outside rather than only from its own
        // cradles — so both the chance of an incomer turning up and the
        // willingness to take one who has no register of his own answer to
        // how much land is going spare. Without this the inbound side of
        // migration was one-directional by construction: a village at the
        // bottom of its cluster (rank.ts) has nobody below it to pull from,
        // so it could only ever export daughters and drain itself.
        const pressure = pressureAt(wantYear);
        // § the preventive check, hard edge: an incomer groom needs a holding
        // to marry onto exactly as a native son does.
        if (pressure >= HOLDINGS_FULL) continue;
        const vacancy = pressure < VACANT_HOLDINGS;
        if (!rng.chance(demo.maleOutMigration.groomPullChance + (vacancy ? 0.1 : 0))) continue;
        if (!exogamyAllowed(pressure)) continue;
        const pulled = pullImmigrantGroom(wantYear, region.marriageM[0] - 3, region.marriageM[1] + 8);
        let M: Person;
        if (pulled) {
          const { srcIdx, cand } = pulled;
          M = addPerson({
            name: cand.name,
            surname: cand.surname,
            sex: "M",
            birth: cand.birth,
            cls: cand.cls,
            father: -1,
            mother: -1,
            incomer: true,
            origin: { regionKey, villageIdx: srcIdx },
            originId: cand.id,
          });
          M.death = { ...cand.death }; // copy of the ORIGIN's canonical roll: the two records never disagree on when he died
          M.riskTrade = cand.riskTrade ?? "normal";
        } else {
          // § the marriage squeeze: nobody in the cluster to pull, so an
          // unaddressable incomer — exactly as the bride path above falls back
          // to, and now on exactly the same terms.
          //
          // This fallback used to require a VACANCY, and the bride path's
          // never did, which quietly made the model a net importer of wives
          // and a net exporter of husbands everywhere. That is not a fact
          // about medieval marriage; it is an artefact of one path being
          // allowed to invent a spouse the register can't address and the
          // other not. It bit hardest where the local supply of real male
          // emigrants was thinnest — the Mediterranean, whose non-heirs stayed
          // near home (`nonHeirBase`) and so gave the cluster fewest grooms to
          // pull — and it was worth most of the inverted celibacy gap:
          // Catalonia took in one husband for every 1.5 wives while England
          // took in more husbands than wives.
          //
          // Nothing is unguarded by removing it: this path still answers to
          // the hard edge of the preventive check (a groom needs a holding,
          // checked above), to the exogamy ceiling, and to groomPullChance —
          // three gates the bride path does not all have.
          const mb = wantYear - rng.int(region.marriageM[0], region.marriageM[1]);
          M = addPerson({
            name: rng.pick(region.maleNames),
            surname: drawSurname(),
            sex: "M",
            birth: mb,
            cls: W.cls,
            father: -1,
            mother: -1,
            incomer: true,
            origin: null,
          });
          M.riskTrade = riskTradeOf(vHash, M.id, M.cls, M.sex);
          M.death = rollDeath(
            makeRng(personStream(vHash, 7001, M.id)),
            mb,
            "M",
            CLASS_INFO[W.cls].wealth,
            region,
            M.riskTrade,
            regionKey,
            villageAddr,
            yieldAt,
          );
          if (M.death.year <= wantYear) {
            const deathYear = wantYear + 1 + rng.int(0, 25);
            M.death = { year: deathYear, age: deathYear - mb, cause: "disease" };
          }
        }
        takenW.add(W.id);
        const c = marry(M, W, wantYear);
        if (c) {
          exogamous++;
          genChildren(c, GENERATION_LAST_YEAR);
        }
      }
    }
  }
  runMatchingRounds();

  // § illegitimacy: a child born to a woman who was not married to its
  // father. Rolled PER YEAR that a woman spends adult and unmarried, not
  // once per woman who ends the solve unmarried, which is what this used to
  // be — and the change is not bookkeeping. The old shape asked one question
  // of one narrow group and produced illegitimate births at 0.06–0.37% of
  // all births, against its own configured intent of 2–3.5% and a literature
  // range of 1–4%. It also put the risk in the wrong place: bastardy was a
  // hazard of the YEARS spent unmarried and of age, which is why the
  // late-marrying NW regions record more of it, and a large share of these
  // children were borne by women who went on to marry someone else entirely
  // — a group a once-per-never-married-woman roll cannot represent at all.
  //
  // Placed AFTER primary matching (so each woman's marriage year, and
  // therefore her real exposure window, is known) but BEFORE the emigration
  // and remarriage passes below: a natural child doesn't stop her from later
  // emigrating or marrying, and all of that still runs normally afterward.
  // The child becomes eligible for the ordinary marriage market too, via the
  // interleaved runMatchingRounds() calls further down.
  //
  // Never assigns an unknown father: biography.ts's native-born birth
  // narration reads `father!`/`mother!` unconditionally for anyone who
  // isn't a founder/incomer (§ pure decode invariant — "both parents are
  // guaranteed present by construction") — inventing an unaddressable
  // father would break that. If no real, non-relative adult man exists yet
  // (only possible in a village's very first generation), the roll is
  // simply skipped for that year.
  const ILLEGITIMATE_MAX = 2;
  /** A woman who bore one natural child did not bear the next the following
   * spring — nursing suppressed conception here exactly as it does in
   * genChildren's own spacing. */
  const ILLEGITIMATE_SPACING = 3;
  function rollIllegitimateBirths(): void {
    for (const W of persons.slice()) {
      if (W.sex !== "F" || W.founder || W.inOrders) continue;
      const r = makeRng(personStream(vHash, 970000, W.id));
      // Her exposure: from an age at which the register would have noticed,
      // until she marries, dies, or the register stops recording births.
      const from = W.birth + Math.max(14, region.marriageF[0] - 3);
      const marriedAt = W.unions?.length ? couples[W.unions[0]].year : Number.POSITIVE_INFINITY;
      const until = Math.min(W.death.year, marriedAt, GENERATION_LAST_YEAR + 1);

      const borneIn = new Set<number>();
      function bear(y: number): void {
        const fatherCandidates = persons.filter(
          (m) =>
            m.sex === "M" &&
            m.id !== W.father &&
            !(m.father === W.father && W.father !== -1) &&
            !(m.mother === W.mother && W.mother !== -1) &&
            m.birth + 14 <= y &&
            m.death.year > y,
        );
        if (!fatherCandidates.length) return;
        const fatherP = r.pick(fatherCandidates);
        const sex: Sex = r.chance(0.5) ? "M" : "F";
        const child = addPerson({
          name: r.pick(sex === "M" ? region.maleNames : region.femaleNames),
          surname: childSurname(fatherP, W),
          sex,
          birth: y,
          cls: W.cls,
          father: fatherP.id,
          mother: W.id,
          illegitimate: true,
        });
        borneIn.add(y);
        child.riskTrade = riskTradeOf(vHash, child.id, child.cls, child.sex);
        child.death = rollDeath(
          makeRng(personStream(vHash, 7001, child.id)),
          y,
          sex,
          CLASS_INFO[child.cls].wealth,
          region,
          child.riskTrade,
          regionKey,
          villageAddr,
          yieldAt,
        );
        // § legitimation: a substantial share of these were exactly a
        // betrothed or courting couple whose child simply arrived before the
        // wedding — if BOTH are still unmarried, roll whether they go on to
        // marry each other after all. Splicing the child into the resulting
        // Couple's own children (not just leaving her found by childrenOf's
        // separate illegitimate-scan) means she's now indistinguishable from
        // any other child of that marriage to siblings/snapshot/succession —
        // exactly the point, since per subsequens matrimonium treated her as
        // legitimate from birth (bar England — see heirEligible above).
        // § the celibate estate: a man in orders is deliberately still
        // eligible to have FATHERED her — a priest's children are among the
        // best-documented illegitimacies there are — but he cannot then
        // marry the mother, so the legitimation branch has to exclude him.
        if (fatherP.spouse == null && !fatherP.inOrders && W.spouse == null && r.chance(0.5)) {
          const marriageYear = y + r.int(1, 2); // strictly after the birth year, never the same year
          const c = marry(fatherP, W, marriageYear);
          if (c) {
            c.children.unshift(child.id);
            child.legitimated = true;
            child.legitimatedYear = marriageYear;
            genChildren(c, GENERATION_LAST_YEAR);
          }
        }
      }

      let lastBirth = Number.NEGATIVE_INFINITY;
      for (let y = from; y < until; y++) {
        if (borneIn.size >= ILLEGITIMATE_MAX) break;
        if (y - lastBirth < ILLEGITIMATE_SPACING) continue;
        if (!r.chance(demo.illegitimacyPerYear)) continue;
        bear(y);
        lastBirth = y;
        // The legitimation branch above may have just married her; her
        // exposure ends there, whatever the loop bound was computed as.
        if (W.spouse != null) break;
      }

      // § childbed deaths: an unmarried woman whose own mortality roll says
      // she died in childbed had a confinement by definition — the register
      // would carry one birth that year. Without this the second childbed
      // pass further down would quietly relabel her death as plain disease.
      // Married women are not this pass's business: recordChildbedConfinements
      // gives them a marital birth instead.
      if (
        W.death.cause === "childbirth" &&
        W.death.year < marriedAt &&
        W.death.year <= GENERATION_LAST_YEAR &&
        !borneIn.has(W.death.year) &&
        W.death.age >= 14
      ) {
        bear(W.death.year);
      }
    }
  }
  rollIllegitimateBirths();
  // A freshly-created illegitimate child is UNPROCESSED — unlike everyone
  // reachable from the founders, who the single exhaustive call above
  // already swept into `processed` one way or another. Without this second
  // call, such a child could reach the emigration passes below (which key
  // off sex/age/spouse only, not `processed`) while still eligible for
  // local matching too, risking the same person being marked BOTH
  // `emigrated` and later locally married by the interleaved loop further
  // down. One more call resolves her (married or processed-but-unmarried)
  // first, same as everyone else by this point.
  runMatchingRounds();

  // Shared by both emigration passes below: a real, resolvable destination
  // address — mostly a short hop to a higher-offset cluster-mate (later
  // pull-able by that village's own solve), occasionally a long jump to
  // another region (still real, just outside anyone's scan range).
  function assignDestination(p: Person): void {
    const offset = clusterOffset(villageIdx);
    if (offset < LOCAL_CLUSTER - 1 && rng.chance(0.8)) {
      p.emigrateTo = { regionKey, villageIdx: clusterBase(villageIdx) + rng.int(offset + 1, LOCAL_CLUSTER - 1) };
      // § the marriage squeeze: someone leaving specifically FOR service in a
      // town should land in one. settlementTypeOf is a plain hash of the
      // address (settlement.ts), so this costs nothing and resolves no
      // envelope — if a market town is within reach up the cluster, prefer
      // it; if the cluster is all farmland, the ordinary draw above stands
      // and she has simply gone into service at the biggest place near her,
      // which is what most of them did.
      if (p.cityService) {
        for (let i = offset + 1; i < LOCAL_CLUSTER; i++) {
          const idx = clusterBase(villageIdx) + i;
          if (settlementTypeOf(worldSeed, regionKey, idx) === "urban") {
            p.emigrateTo = { regionKey, villageIdx: idx };
            break;
          }
        }
      }
    } else {
      // § the far end (migration.ts): the destination is now COMPUTED, by
      // an invertible pairing with the region one rung up, rather than
      // rolled. `rng.int(0, 200)` produced an address no village could
      // work backwards from, which is precisely why a long-distance
      // emigrant was a dead end: the far end had no way to know she was
      // coming without searching every village of every region below it.
      // The roll is still consumed so the draw count is unchanged.
      const higher = higherRankRegions(regionKey);
      const paired = longDistanceDestination(worldSeed, regionKey, villageIdx);
      if (higher.length && rng.chance(0.6)) {
        // BOTH legacy draws are still consumed, in their original order.
        // Taking only one of them shifted the shared stream and moved the
        // aggregate demography off its bands — the destination address is
        // the only thing that should be changing here.
        const fallbackRegion = rng.pick(higher);
        const fallbackIdx = rng.int(0, 200);
        p.emigrateTo = paired ?? { regionKey: fallbackRegion, villageIdx: fallbackIdx };
        p.longDistance = true;
      } else {
        p.emigrateTo = { regionKey, villageIdx: clusterBase(villageIdx) + LOCAL_CLUSTER + rng.int(0, LOCAL_CLUSTER - 1) };
        p.longDistance = true; // outside this cluster's own scan range: narrated, not pulled
      }
    }
  }

  // Native adults unmatched after the PRIMARY rounds: emigration (§11),
  // decided entirely by this village's own solve — never by a destination
  // reaching back in. A famine or war year raises the chance, so a bad
  // harvest here and a swollen neighbour there stay consistent with each
  // other for free. Deliberately placed BEFORE remarriage (not after): a
  // large surplus of unmatched native women after primary matching is
  // expected by design (men often import a bride instead of marrying
  // locally) — emigration is its normal outlet, at a normal age. Running
  // remarriage first would instead sweep that whole backlog into
  // increasingly old widower-marriages before it ever got the chance to
  // leave, skewing the marriage-age statistics badly.
  for (const W of persons) {
    // § the celibate estate: a woman in religion doesn't marry out — she has
    // already left the marriage market by another door, and her house is the
    // cloister, not another parish's.
    if (W.sex !== "F" || W.founder || W.inOrders || W.spouse != null || W.emigrated || W.death.age < region.marriageF[1]) continue;
    const atYear = W.birth + region.marriageF[1];
    // § the harvest: a hard year is one the crop actually failed in,
    // wherever and whenever that was — not one inside a fixed window. This
    // read famineAt(), and narrowing that window to the Great Famine proper
    // quietly cut the years in which people were pushed off the land, which
    // is the mechanism this rate exists to represent: a dearth emptied
    // parishes, and it is why the emigration rate has a pressured branch.

    const pressured = yieldAt(atYear) < DEARTH || !!warAt(atYear, region);
    // § the preventive check: emigration is the release valve for crowding,
    // so it has to answer to it. A village with tenements standing empty
    // after the plague kept its daughters; one with none to give could not.
    const base = (pressured ? demo.emigration.pressured : demo.emigration.base) * emigrationMult(pressureAt(atYear));
    // § the marriage squeeze: two draws, because a departure is not always a
    // marriage. The first is the old one — she married out of the parish. The
    // second is the outlet this model simply did not have: she went into
    // service in a town. Both are drawn unconditionally, so which branch is
    // taken never changes the shared stream's draw count (the same discipline
    // genChildren's bridal-pregnancy roll follows).
    //
    // Without the second draw the only thing an unmatched daughter could do
    // was marry elsewhere or stay a lay spinster to the end, and the
    // Mediterranean — where households held to their land hardest, and where
    // a spousal age gap of five to eleven years produced the largest surplus
    // of unmarried young women in the model — came out with the HIGHEST
    // permanent female celibacy in Europe. The reverse is the case: the
    // European Marriage Pattern's whole signature is late, incomplete
    // marriage in the north-west and early, near-universal marriage in the
    // south. The daughters were leaving; they just weren't leaving to marry.
    const marriesOut = rng.chance(base);
    // § the preventive check: service in a town answers to crowding exactly as
    // marrying out does, and it has to, or it is not an outlet at all — the
    // village is held at its own ceiling (capacity.ts), so relieving the
    // female surplus by any fixed amount simply grows the population until
    // the surplus comes back. A rate that RISES with pressure is what makes
    // this equilibrate: the fuller the parish, the harder it sent its
    // daughters out to service, which is also the plain sense of it.
    const toService = rng.chance(demo.cityService * emigrationMult(pressureAt(atYear)));
    if (marriesOut || toService) {
      W.emigrated = true;
      if (marriesOut) W.marriedOut = true;
      else W.cityService = true;
      assignDestination(W);
    }
  }

  // § male out-migration: the landless-younger-son safety valve, same
  // ordering rationale as the female pass above. Non-heirs who found no
  // local match leave far more often than heirs, who had a tenement to
  // hold and stayed even unmarried. `marriedOut` is deliberately left
  // false: he isn't narrated as having married out, just left, though he
  // may yet be pulled as a real immigrant groom by a village further down
  // the cluster (the groom-pull step inside the matching rounds above).
  for (const M of persons) {
    if (M.sex !== "M" || M.founder || M.spouse != null || M.emigrated || M.inOrders) continue;
    if (M.death.age < region.marriageM[1]) continue;
    const heir = isHeir(persons, region, regionKey, M);
    const atYear = M.birth + region.marriageM[1];
    // § the harvest: a hard year is one the crop actually failed in,
    // wherever and whenever that was — not one inside a fixed window. This
    // read famineAt(), and narrowing that window to the Great Famine proper
    // quietly cut the years in which people were pushed off the land, which
    // is the mechanism this rate exists to represent: a dearth emptied
    // parishes, and it is why the emigration rate has a pressured branch.
    const pressured = yieldAt(atYear) < DEARTH || !!warAt(atYear, region);
    const chance = pressured ? demo.maleOutMigration.pressured : heir ? demo.maleOutMigration.heirBase : demo.maleOutMigration.nonHeirBase;
    // § the preventive check: same valve as the women's pass above. A
    // younger son with no holding to wait for left; a younger son in a
    // village half-emptied by plague had a vacant tenement to take up.
    if (!rng.chance(chance * emigrationMult(pressureAt(atYear)))) continue;
    M.emigrated = true;
    assignDestination(M);
  }

  // § remarriage: widowhood in mid-life was routinely followed by
  // remarriage, at region-specific rates (demography.ts) — high for men
  // everywhere, high for women in NW Europe, low in the dowry-regime
  // Mediterranean. Depth is NOT capped at one remarriage: `latestWidowedAt`
  // reads the person's MOST RECENT union rather than requiring exactly one,
  // so someone widowed a second (or third) time gets the same chance again.
  function latestWidowedAt(p: Person): number | null {
    if (!p.unions?.length) return null;
    const c = couples[p.unions[p.unions.length - 1]];
    const other = persons[p.id === c.husband ? c.wife : c.husband];
    if (other.death.year >= p.death.year) return null;
    return other.death.year;
  }
  // "currently unmarried" = never married, or widowed with no union since.
  function isCurrentlyUnmarried(p: Person): boolean {
    if (!p.unions?.length) return p.spouse == null;
    return latestWidowedAt(p) != null;
  }

  function matchWidowers(takenSpouse: Set<number>): boolean {
    let any = false;
    const widowers = persons
      .map((m) => ({ m, lost: m.sex === "M" && !m.inOrders && !m.emigrated && !takenSpouse.has(m.id) ? latestWidowedAt(m) : null }))
      .filter((x): x is { m: Person; lost: number } => x.lost != null && x.lost - x.m.birth <= 58)
      .sort((a, b) => a.lost - b.lost || a.m.id - b.m.id);
    for (const { m, lost } of widowers) {
      if (takenSpouse.has(m.id)) continue; // matched earlier in this same pass
      // § remarriage: rolled once per BEREAVEMENT, off the person's own
      // stream, rather than once per pass off the shared one. The passes
      // re-scan the same widower up to a dozen times (three passes inside a
      // phase, four interleaved phases), and a fresh roll each time drove
      // the effective rate toward 1 for everyone — which flattened exactly
      // the difference between men's and women's remarriage that
      // demography.ts's region table exists to express, to the point where
      // Germany's widows came out marginally likelier to remarry than its
      // widowers on a configured 0.55 against 0.30. Keying the namespace on
      // the year of the loss keeps a second or third widowhood a genuinely
      // new roll.
      if (!makeRng(personStream(vHash, 960000 + lost, m.id)).chance(demo.remarry.M)) continue;
      const year = lost + 1 + rng.int(0, 2);
      if (year >= m.death.year) continue;
      let best: Person | null = null,
        bestScore = 1e9;
      for (const W of persons) {
        if (W.sex !== "F" || W.founder || W.inOrders || W.marriedOut || W.emigrated) continue;
        if (takenSpouse.has(W.id) || !isCurrentlyUnmarried(W)) continue;
        // `year` is drawn from M's OWN bereavement timeline — a widowed W is
        // only actually free to take it if SHE was already free strictly
        // before it too (isCurrentlyUnmarried alone only says she EVENTUALLY
        // outlives her current spouse, not that she already has by `year`).
        const wLost = latestWidowedAt(W);
        if (wLost != null && wLost >= year) continue;
        if (W.father === m.id || W.mother === m.id) continue; // never a daughter
        if (W.father === m.father && m.father !== -1) continue; // never a sister (paternal)
        if (W.mother === m.mother && m.mother !== -1) continue; // never a sister (maternal)
        if (W.death.year <= year) continue;
        const age = year - W.birth;
        // § the marriage squeeze: the floor is the region's own earliest
        // marriage age, not a flat 18. In Tuscany that is fifteen, and a flat
        // 18 excluded from the widower's market precisely the girls the
        // catasto shows filling it.
        if (age < Math.min(18, region.marriageF[0]) || age > 45) continue;
        const isWidow = wLost != null;
        // § remarriage vs. the emigration backlog: emigration (above) already
        // ran first and drained most of the surplus never-married women at a
        // normal age, so what's left here is mostly genuine widows plus
        // whoever stayed by choice.
        //
        // § the marriage squeeze: who he takes from that pool is regional
        // (demography.ts's `widowerBride`), and it used to be hard-coded to
        // the NW-European answer — a widow, or a never-married woman already
        // past the normal local marriage window (an "old maid" he took on),
        // in preference to a young single woman. Under the dowry regime the
        // preference runs the other way and the gap he marries across is far
        // wider, which is exactly the mechanism that absorbed the surplus of
        // young unmarried women that regime's own wide spousal age gap
        // created. Blocking it was most of why the Mediterranean regions came
        // out with more permanent lay spinsters than England.
        const stillYoung = !isWidow && age <= region.marriageF[1] + 4;
        const bride = demo.widowerBride;
        const wanted = Math.min(year - m.birth - bride.gap, bride.cap);
        const score = Math.abs(age - wanted) + (isWidow ? 0 : 2) + (stillYoung ? bride.maiden : 0) + consanguinityPenalty(persons, m, W);
        if (score < bestScore) {
          bestScore = score;
          best = W;
        }
      }
      if (!best) continue;
      if (latestWidowedAt(best) != null && !rng.chance(Math.min(1, demo.remarry.F / Math.max(demo.remarry.M, 0.01)))) continue;
      // § affinity: computed BEFORE marry() — marry() pushes the NEW union
      // onto m.unions immediately, so isAffinal called after it would read
      // lastSpouseOf(m) as `best` herself (a trivial self-comparison,
      // nearly always true) instead of m's actual previous, deceased wife.
      const affinal = isAffinal(persons, couples, m, best);
      takenSpouse.add(best.id);
      takenSpouse.add(m.id);
      const c = marry(m, best, year);
      if (c) {
        if (affinal) c.affinal = true; // she's a sister of his own late wife
        genChildren(c, GENERATION_LAST_YEAR);
      }
      any = true;
    }
    return any;
  }

  function matchWidows(takenSpouse: Set<number>): boolean {
    let any = false;
    // widows left un-courted above may still remarry a never-married man
    const widows = persons
      .map((w) => ({ w, lost: w.sex === "F" && !w.marriedOut && !w.emigrated && !takenSpouse.has(w.id) ? latestWidowedAt(w) : null }))
      .filter((x): x is { w: Person; lost: number } => x.lost != null && x.lost - x.w.birth <= 45)
      .sort((a, b) => a.lost - b.lost || a.w.id - b.w.id);
    for (const { w, lost } of widows) {
      if (takenSpouse.has(w.id)) continue;
      // § remarriage: once per bereavement, same as matchWidowers above.
      if (!makeRng(personStream(vHash, 961000 + lost, w.id)).chance(demo.remarry.F)) continue;
      const year = lost + 1 + rng.int(0, 3);
      if (year >= w.death.year) continue;
      let best: Person | null = null,
        bestScore = 1e9;
      for (const M of persons) {
        if (M.sex !== "M" || M.founder || M.inOrders || M.emigrated || !isCurrentlyUnmarried(M)) continue;
        if (takenSpouse.has(M.id)) continue;
        // symmetric to matchWidowers: `year` comes from W's own bereavement
        // timeline, so a widowed M must already be free strictly before it.
        const mLost = latestWidowedAt(M);
        if (mLost != null && mLost >= year) continue;
        if (M.father === w.id || M.mother === w.id) continue; // never a son
        if (M.father === w.father && w.father !== -1) continue; // never a brother (paternal)
        if (M.mother === w.mother && w.mother !== -1) continue; // never a brother (maternal)
        if (M.death.year <= year) continue;
        const age = year - M.birth;
        if (age < 22 || age > 60) continue;
        const score = Math.abs(age - (year - w.birth)) + consanguinityPenalty(persons, M, w);
        if (score < bestScore) {
          bestScore = score;
          best = M;
        }
      }
      if (!best) continue;
      // § affinity: computed BEFORE marry(), same reason as matchWidowers above.
      const affinal = isAffinal(persons, couples, w, best);
      takenSpouse.add(best.id);
      takenSpouse.add(w.id);
      const c = marry(best, w, year);
      if (c) {
        if (affinal) c.affinal = true; // he's a brother of her own late husband
        genChildren(c, GENERATION_LAST_YEAR);
      }
      any = true;
    }
    return any;
  }

  // § remarriage depth: run widower- then widow-matching passes to a small
  // fixed depth, re-scanning each time so someone widowed a SECOND or third
  // time (by their new spouse's independently-rolled death, or by a spouse
  // gained in a later pass) gets the same remarriage chance as the first
  // time — not just the initial widowhood.
  function remarriagePhase(): boolean {
    const takenSpouse = new Set<number>();
    let anyEver = false;
    for (let pass = 0; pass < 3; pass++) {
      const a = matchWidowers(takenSpouse);
      const b = matchWidows(takenSpouse);
      if (a || b) anyEver = true;
      else break;
    }
    return anyEver;
  }
  // Interleaved with matching rounds (not a single one-shot phase): a
  // remarriage can produce children who come of age and need matching, and
  // THAT matching can itself create new couples who are later widowed and
  // eligible to remarry in turn. Bounded and self-terminating (each
  // iteration that changes nothing stops the loop).
  for (let i = 0; i < 4; i++) {
    const any = remarriagePhase();
    runMatchingRounds();
    if (!any) break;
  }

  // =====================================================================
  // § childbed deaths. mortality.ts applies a real maternal hazard across a
  // woman's WHOLE fertile-age ramp — it has to: death is rolled before the
  // marriage matching that would tell it whether she ever bore a child at
  // all. That leaves the CAUSE label running ahead of the register's own
  // facts, and a "died in childbed" entry on the page of a woman with no
  // confinement that year, often no husband and no child ever, reads as a
  // straightforward contradiction.
  //
  // Both passes below settle it from opposite ends, and neither touches the
  // hazard itself — the same women die in the same years, so the life table
  // and every band in demography.test.ts are unaffected.
  // =====================================================================
  function boreChildIn(W: Person, year: number): boolean {
    return persons.some((p) => p.mother === W.id && p.birth === year);
  }

  // The union she was actually in that year: married strictly before it, and
  // a husband still living — or dead within the year before, which is what a
  // posthumous child is.
  function unionAliveAt(W: Person, year: number): Couple | null {
    if (!W.unions?.length) return null;
    for (let i = W.unions.length - 1; i >= 0; i--) {
      const c = couples[W.unions[i]];
      if (c.year < year && persons[c.husband].death.year >= year - 1) return c;
    }
    return null;
  }

  // Pass one — where she really was in a marriage that year, record the
  // birth the label implies. A last confinement that kills the mother is
  // precisely what the cause describes, and the child it leaves is an
  // ordinary register entry: named, parented, and (after the matching call
  // below) eligible for the marriage market like anyone else. Runs for a
  // pulled residence record too, whose copied cause came from an origin
  // village that never saw the marriage this one recorded.
  function recordChildbedConfinements(): void {
    for (const W of persons.slice()) {
      if (W.sex !== "F" || W.death.cause !== "childbirth") continue;
      const y = W.death.year;
      if (y > GENERATION_LAST_YEAR || boreChildIn(W, y)) continue;
      const c = unionAliveAt(W, y);
      if (!c || c.children.length >= 12) continue;
      makeChild(c, persons[c.husband], W, y);
    }
  }
  recordChildbedConfinements();
  runMatchingRounds();

  // Pass two — whatever pass one could not give a real confinement (she
  // never married, or was already widowed, or the register era closed first)
  // was never a childbed death, so only the LABEL moves. Left alone for a
  // residence record, whose cause is its origin register's canonical fact
  // and was already settled by that village's own solve, and for an
  // emigrant, whose confinement — if there was one — belongs to a register
  // this village never kept.
  for (const W of persons) {
    if (W.sex !== "F" || W.death.cause !== "childbirth") continue;
    if (W.originId != null || W.emigrated) continue;
    if (!boreChildIn(W, W.death.year)) W.death = { ...W.death, cause: "disease" };
  }

  // § service placement (service.ts): the two facts about a service spell
  // that only the finished matching can supply — the year it ended (the
  // servant's own marriage) and the house it was served in. Both draw from
  // per-person streams of their own, so neither perturbs anything above.
  resolveServiceSpells(persons, couples, vHash);
  assignServiceMasters(persons, couples, vHash);

  const leftover = persons.filter((p) => !p.founder && !p.inOrders && p.death.age >= 16 && p.spouse == null && !processed.has(p.id));
  const diagnostics = { matchingRounds, truncated: leftover.length > 0, unmatched: leftover.length };

  // index couples on persons for O(1) FIRST-marriage lookup (full history
  // is on Person.unions)
  const coupleOf: Record<number, number> = {};
  couples.forEach((c, i) => {
    if (coupleOf[c.husband] === undefined) coupleOf[c.husband] = i;
    if (coupleOf[c.wife] === undefined) coupleOf[c.wife] = i;
  });

  return { worldSeed, regionKey, villageIdx, vHash, place, region, persons, couples, coupleOf, diagnostics };
}
